import React, { useEffect, useMemo, useState } from 'react';
import { X, ArrowRightLeft, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { workersApi, companiesApi } from '../lib/api';

/**
 * Dialog per riassegnare manualmente il ``companyId`` di un lavoratore.
 * Propaga in cascata su formazione/visite/dpi/haccp/sicurezza lato backend.
 *
 * Props:
 *  - open, onOpenChange
 *  - worker: oggetto lavoratore con {id, firstName, lastName, companyId}
 *  - currentCompanyName: opzionale, per mostrare l'origine
 *  - onDone: callback dopo il salvataggio
 */
const ReassignCompanyDialog = ({ open, onOpenChange, worker, currentCompanyName, onDone }) => {
  const [companies, setCompanies] = useState([]);
  const [target, setTarget] = useState('');
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTarget('');
    setSearch('');
    companiesApi.list({ status: 'all' })
      .then((r) => setCompanies(r.data || []))
      .catch(() => setCompanies([]));
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (companies || [])
      .filter((c) => c.id && c.id !== worker?.companyId)
      .filter((c) => !q || (c.name || '').toLowerCase().includes(q))
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'it', { sensitivity: 'base' }));
  }, [companies, search, worker]);

  if (!open || !worker) return null;

  const save = async () => {
    if (!target) return;
    setSaving(true);
    try {
      const { data } = await workersApi.reassignCompany(worker.id, target);
      const cascade = data?.cascade || {};
      const total = Object.values(cascade).reduce((a, b) => a + (b || 0), 0);
      toast.success(`Lavoratore spostato · ${total} record collegati riallineati`);
      onOpenChange(false);
      onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore riassegnazione');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="reassign-company-dialog">
      <div className="absolute inset-0 bg-black/50" onClick={() => onOpenChange(false)} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-[min(96vw,560px)] max-h-[85vh] flex flex-col">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-[#023F80] flex items-center gap-2">
              <ArrowRightLeft size={18} /> Sposta lavoratore
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {worker.lastName} {worker.firstName}
              {currentCompanyName ? ` · attualmente in ${currentCompanyName}` : ''}
            </p>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            className="p-1.5 rounded-md hover:bg-slate-100"
            data-testid="reassign-company-close"
          >
            <X size={16} className="text-slate-500" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-slate-100">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca azienda destinazione…"
              data-testid="reassign-company-search"
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-100"
            />
          </div>
          <p className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
            Tutti i corsi di formazione, visite mediche, DPI e altri record del lavoratore verranno riallineati alla nuova azienda. Operazione tracciata in audit log.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          <div className="grid grid-cols-1 gap-1.5">
            {filtered.map((c) => {
              const isSel = target === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => setTarget(c.id)}
                  data-testid={`reassign-company-opt-${c.id}`}
                  className={`text-left px-3 py-2 rounded-lg border text-sm transition-colors ${
                    isSel
                      ? 'bg-indigo-50 border-indigo-300 text-indigo-900 font-semibold'
                      : 'bg-white border-slate-200 hover:border-indigo-200 hover:bg-indigo-50/40'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`inline-block w-3.5 h-3.5 rounded-full border ${isSel ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300'}`} />
                    <span className="truncate">{c.name}</span>
                    {c.status === 'dismesso' && (
                      <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">dismessa</span>
                    )}
                  </div>
                </button>
              );
            })}
            {filtered.length === 0 && (
              <p className="text-xs text-slate-400 text-center py-4">Nessuna azienda disponibile</p>
            )}
          </div>
        </div>

        <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-end gap-2">
          <button
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100"
            data-testid="reassign-company-cancel"
          >
            Annulla
          </button>
          <button
            onClick={save}
            disabled={!target || saving}
            data-testid="reassign-company-save"
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#023F80] text-white disabled:opacity-60 flex items-center gap-2"
          >
            {saving ? <><Loader2 size={14} className="animate-spin" /> Salvo…</> : <>Sposta lavoratore</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReassignCompanyDialog;
